import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

/** A dart is thrown from the corner and lands in the bullseye (the target at the centre of the logo). */
const RINGS = [
  { r: 62, fill: "#8C1C22" },
  { r: 52, fill: "#FCFBD4" },
  { r: 42, fill: "#8C1C22" },
  { r: 31, fill: "#FCFBD4" },
  { r: 20, fill: "#8C1C22" },
  { r: 9, fill: "#FCFBD4" },
];

function Board() {
  return (
    <svg width="148" height="148" viewBox="-74 -74 148 148" aria-hidden>
      <circle r="72" fill="#FBF7F4" stroke="#8C1C22" strokeWidth="2"/>
      {Array.from({ length: 16 }).map((_, i) => {
        const a = (i * Math.PI) / 8;
        return <line key={i} x1={Math.cos(a) * 62} y1={Math.sin(a) * 62} x2={Math.cos(a) * 71} y2={Math.sin(a) * 71} stroke="#FCFBD4" strokeWidth="1.5"/>;
      })}
      {RINGS.map((g) => <circle key={g.r} r={g.r} fill={g.fill}/>)}
    </svg>
  );
}

function Dart() {
  // drawn pointing left; the tip sits at (0, 16)
  return (
    <svg width="128" height="32" viewBox="0 0 128 32" aria-hidden style={{ overflow: "visible" }}>
      <polygon points="0,16 16,13 16,19" fill="#5C3B3D"/>
      <rect x="16" y="12" width="40" height="8" rx="3" fill="#4D0E12"/>
      <rect x="24" y="12" width="3" height="8" fill="#FCFBD4"/>
      <rect x="33" y="12" width="3" height="8" fill="#FCFBD4"/>
      <rect x="42" y="12" width="3" height="8" fill="#FCFBD4"/>
      <rect x="56" y="14.5" width="40" height="3" fill="#8C1C22"/>
      <polygon points="90,16 128,0 120,16" fill="#FCFBD4"/>
      <polygon points="90,16 128,32 120,16" fill="#FCFBD4"/>
      <polygon points="92,16 122,8 118,16" fill="#A11E23" opacity=".85"/>
    </svg>
  );
}

const SEEN_KEY = "dmv_intro_seen_v1";

function alreadySeen() {
  try { if (localStorage.getItem(SEEN_KEY)) return true; } catch {}
  try { if (sessionStorage.getItem(SEEN_KEY)) return true; } catch {}
  return false;
}
function markSeen() {
  try { localStorage.setItem(SEEN_KEY, String(Date.now())); } catch {}
  try { sessionStorage.setItem(SEEN_KEY, "1"); } catch {}
}

/**
 * Welcome moment: plays ONCE, the very first time someone opens the app on a device.
 * It never plays on page changes. Skipped for people who prefer reduced motion,
 * and when the app is opened straight into a dashboard.
 */
export default function DartTransition() {
  const [visible, setVisible] = useState(false);
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dash = /^\/(owner|admin|account)/.test(window.location.pathname);
    if (alreadySeen() || reduce || dash) { markSeen(); return; }
    markSeen();
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[80] pointer-events-none flex items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.35 } }} transition={{ duration: 0.12 }}
          data-testid="dart-intro"
        >
          <div className="absolute inset-0 bg-[color:var(--cream-primary)]/90 backdrop-blur-[3px]"/>
          <div className="relative" style={{ width: 0, height: 0 }}>
            <motion.div className="absolute" style={{ left: -74, top: -74 }} initial={{ scale: 0.55, rotate: -40, opacity: 0 }} animate={{ scale: [0.55, 1, 1, 1.06, 1], rotate: [-40, 0, 0, 0, 0], opacity: 1 }} transition={{ duration: 0.75, times: [0, 0.25, 0.6, 0.7, 1], ease: "easeOut" }}>
              <Board/>
            </motion.div>
            {/* impact ripple */}
            <motion.span className="absolute rounded-full border-2 border-[color:var(--gold)]" style={{ left: -12, top: -12, width: 24, height: 24 }} initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 0, 5.5], opacity: [0, 0.9, 0] }} transition={{ duration: 0.75, times: [0, 0.6, 1] }}/>
            {/* the dart */}
            <motion.div className="absolute" style={{ left: 0, top: -16, transformOrigin: "0px 16px" }}
              initial={{ x: "58vw", y: "42vh", rotate: 38, opacity: 0 }}
              animate={{ x: ["58vw", "26vw", "0vw", "0vw", "0vw", "0vw"], y: ["42vh", "-6vh", "0vh", "0vh", "0vh", "0vh"], rotate: [38, 14, -10, -16, -8, -11], opacity: [0, 1, 1, 1, 1, 1] }}
              transition={{ duration: 0.72, times: [0, 0.32, 0.55, 0.66, 0.78, 1], ease: "easeOut" }}>
              <Dart/>
            </motion.div>
            <motion.div className="absolute whitespace-nowrap text-center font-display text-[26px] text-[color:var(--maroon)]" style={{ left: 0, top: 96 }}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.4 }}>
              <span style={{ display: "inline-block", transform: "translateX(-50%)" }}>Dart<span className="text-[color:var(--gold)]">My</span>Venue</span>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
